import { MAP_MARKER_COLORS } from './mapMarkerColors';

/** Vehicle fill by operational status (manager map). */
export function vehicleMarkerFill(status) {
  switch (status) {
    case 'idle':
      return '#546E7A';
    case 'refueling':
      return '#FB8C00';
    case 'out_of_service':
      return '#78909C';
    case 'active':
    default:
      return MAP_MARKER_COLORS.vehicle;
  }
}

function svgDataUrl(svg) {
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg.trim())}`;
}

/**
 * Top-down box truck — the truck shape IS the marker (no circle badge).
 * Front of cab points north at 0° rotation; rotates with GPS heading.
 */
export function getVehicleMarkerIcon(maps, options = {}) {
  if (!maps?.Size || !maps?.Point) return null;

  const {
    status = 'active',
    rotation = 0,
    size = 56,
  } = options;

  const fill = vehicleMarkerFill(status);
  const cabDark = shadeColor(fill, -18);
  const stroke = '#FFFFFF';
  const wheel = '#212121';
  const window = '#BBDEFB';

  const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 48 48">
  <defs>
    <filter id="vShadow" x="-35%" y="-35%" width="170%" height="170%">
      <feDropShadow dx="0" dy="2" stdDeviation="1.6" flood-color="#000000" flood-opacity="0.38"/>
    </filter>
  </defs>
  <g filter="url(#vShadow)" transform="rotate(${rotation} 24 24)">
    <!-- rear axle wheels -->
    <circle cx="10.5" cy="33.5" r="4.2" fill="${wheel}" stroke="${stroke}" stroke-width="1.6"/>
    <circle cx="37.5" cy="33.5" r="4.2" fill="${wheel}" stroke="${stroke}" stroke-width="1.6"/>
    <!-- cargo / tank body -->
    <rect x="12.5" y="21" width="23" height="15.5" rx="2.8" fill="${fill}" stroke="${stroke}" stroke-width="2.2"/>
    <rect x="15" y="24" width="18" height="2" rx="1" fill="${stroke}" opacity="0.35"/>
    <!-- front axle wheels -->
    <circle cx="10.5" cy="17.5" r="4.2" fill="${wheel}" stroke="${stroke}" stroke-width="1.6"/>
    <circle cx="37.5" cy="17.5" r="4.2" fill="${wheel}" stroke="${stroke}" stroke-width="1.6"/>
    <!-- cab -->
    <path d="M14 10.5h20c2.2 0 4 1.8 4 4v7.5H10V14.5c0-2.2 1.8-4 4-4z" fill="${cabDark}" stroke="${stroke}" stroke-width="2.2" stroke-linejoin="round"/>
    <!-- windshield (front = top) -->
    <rect x="16.5" y="12" width="15" height="6.5" rx="1.8" fill="${window}" stroke="${stroke}" stroke-width="1.4"/>
    <!-- front bumper — makes direction obvious -->
    <rect x="17" y="6.5" width="14" height="3.2" rx="1.6" fill="${stroke}"/>
    <rect x="21" y="5.2" width="6" height="2" rx="1" fill="${fill}" stroke="${stroke}" stroke-width="1"/>
  </g>
</svg>`;

  return {
    url: svgDataUrl(svg),
    scaledSize: new maps.Size(size, size),
    anchor: new maps.Point(size / 2, size / 2),
  };
}

/** Darken/lighten a hex color by percent (-100 to 100). */
function shadeColor(hex, percent) {
  const num = parseInt(hex.replace('#', ''), 16);
  if (Number.isNaN(num)) return hex;
  const r = Math.min(255, Math.max(0, (num >> 16) + Math.round(2.55 * percent)));
  const g = Math.min(255, Math.max(0, ((num >> 8) & 0x00ff) + Math.round(2.55 * percent)));
  const b = Math.min(255, Math.max(0, (num & 0x0000ff) + Math.round(2.55 * percent)));
  return `#${(0x1000000 + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

export function getJobMarkerIcon(maps) {
  if (!maps?.Size || !maps?.Point) return null;

  const size = 34;
  const fill = MAP_MARKER_COLORS.job;

  const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 32 32">
  <circle cx="16" cy="16" r="13" fill="${fill}" stroke="#FFFFFF" stroke-width="2.5"/>
  <path fill="#FFFFFF" d="M16 8.5a4.5 4.5 0 0 0-4.5 4.5c0 3.4 4.5 8.5 4.5 8.5s4.5-5.1 4.5-8.5A4.5 4.5 0 0 0 16 8.5zm0 6.1a1.6 1.6 0 1 1 0-3.2 1.6 1.6 0 0 1 0 3.2z"/>
</svg>`;

  return {
    url: svgDataUrl(svg),
    scaledSize: new maps.Size(size, size),
    anchor: new maps.Point(size / 2, size / 2),
  };
}

export function getDepotMarkerIcon(maps) {
  if (!maps?.Size || !maps?.Point) return null;

  const size = 36;
  const fill = MAP_MARKER_COLORS.depot;

  const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 36 36">
  <circle cx="18" cy="18" r="15" fill="${fill}" stroke="#FFFFFF" stroke-width="2.5"/>
  <path fill="#FFFFFF" d="M18 8c-4.2 0-7.5 3-7.5 6.7 0 5.2 7.5 13.3 7.5 13.3s7.5-8.1 7.5-13.3C25.5 11 22.2 8 18 8zm0 9.2a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z"/>
  <rect x="14" y="24" width="8" height="3" rx="0.8" fill="#FFFFFF" opacity="0.9"/>
</svg>`;

  return {
    url: svgDataUrl(svg),
    scaledSize: new maps.Size(size, size),
    anchor: new maps.Point(size / 2, size / 2),
  };
}

/**
 * @param {typeof google.maps} maps
 * @param {'vehicle'|'driver'|'job'|'depot'} type
 * @param {object} [data] marker payload (e.g. vehicle status, heading)
 */
export function getMapMarkerIcon(maps, type, data = {}) {
  if (!maps) return null;

  switch (type) {
    case 'vehicle':
    case 'driver':
      return getVehicleMarkerIcon(maps, {
        status: data.status || 'active',
        rotation: data.heading ?? data.course ?? data.vehicle_location?.heading ?? 0,
      });
    case 'job':
      return getJobMarkerIcon(maps);
    case 'depot':
      return getDepotMarkerIcon(maps);
    default:
      return null;
  }
}
